/**
 * URL Utilities for the Service Directory
 *
 * Provides helper functions for building URLs including Google Maps links,
 * phone URLs, and URL formatting/normalization.
 *
 * @module lib/url-utils
 */

/**
 * Build a Google Maps URL for a given address.
 * Creates a search URL that will locate and display the address on Google Maps.
 *
 * @param address - Full address string to locate on Google Maps
 * @returns Google Maps search URL
 *
 * @example
 * ```ts
 * const mapsUrl = buildGoogleMapsUrl('123 Main St, Columbus, OH 43215');
 * // Returns: 'https://www.google.com/maps/search/?api=1&query=123+Main+St%2C+Columbus%2C+OH+43215'
 * ```
 */
export function buildGoogleMapsUrl(address: string): string {
  const encodedAddress = encodeURIComponent(address);
  return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
}

/**
 * Build a tel: URL for a phone number.
 * Strips all non-numeric characters except leading + for international numbers.
 *
 * @param phone - Phone number in any format
 * @returns tel: URL for phone dialing
 *
 * @example
 * ```ts
 * const phoneUrl = buildPhoneUrl('(614) 555-1234');
 * // Returns: 'tel:+16145551234'
 *
 * const intlPhone = buildPhoneUrl('+1 (614) 555-1234');
 * // Returns: 'tel:+16145551234'
 * ```
 */
export function buildPhoneUrl(phone: string): string {
  // Check if the number starts with + (international format)
  const hasPlus = phone.trim().startsWith('+');

  // Remove all non-numeric characters
  const digits = phone.replace(/\D/g, '');

  // If no plus and it's a 10-digit US number, add +1
  if (!hasPlus && digits.length === 10) {
    return `tel:+1${digits}`;
  }

  // If it already has the country code or plus
  if (hasPlus) {
    return `tel:+${digits}`;
  }

  // Return as-is for other formats
  return `tel:${digits}`;
}

/**
 * Ensure a URL has the https:// protocol.
 * Handles various input formats including bare domains, http://, and https://.
 *
 * @param url - URL string to normalize
 * @returns URL with https:// protocol
 *
 * @example
 * ```ts
 * ensureHttps('example.com'); // 'https://example.com'
 * ensureHttps('http://example.com'); // 'https://example.com'
 * ensureHttps('https://example.com'); // 'https://example.com'
 * ensureHttps('www.example.com'); // 'https://www.example.com'
 * ```
 */
export function ensureHttps(url: string): string {
  const trimmed = url.trim();

  // Already has https
  if (trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Has http, upgrade to https
  if (trimmed.startsWith('http://')) {
    return trimmed.replace('http://', 'https://');
  }

  // Has protocol-relative URL (//example.com)
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }

  // No protocol, add https
  return `https://${trimmed}`;
}

/**
 * Format a phone number for display.
 * Converts various phone number formats to a consistent (XXX) XXX-XXXX format
 * for US numbers, or preserves international format.
 *
 * @param phone - Phone number in any format
 * @returns Formatted phone number string
 *
 * @example
 * ```ts
 * formatPhoneNumber('6145551234'); // '(614) 555-1234'
 * formatPhoneNumber('614-555-1234'); // '(614) 555-1234'
 * formatPhoneNumber('+1 614 555 1234'); // '(614) 555-1234'
 * formatPhoneNumber('+44 20 7946 0958'); // '+44 20 7946 0958' (non-US preserved)
 * ```
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-numeric characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Extract digits only for length check
  const digits = phone.replace(/\D/g, '');

  // US number: 10 digits or 11 digits starting with 1
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    const localNumber = digits.slice(1);
    return `(${localNumber.slice(0, 3)}) ${localNumber.slice(3, 6)}-${localNumber.slice(6)}`;
  }

  // Non-US or unknown format: return cleaned version with original formatting hints
  // If it starts with +, preserve international format
  if (cleaned.startsWith('+')) {
    // Try to preserve some formatting for international numbers
    return phone.trim();
  }

  // Return original if format is unknown
  return phone.trim();
}

/**
 * Build a mailto: URL for an email address.
 *
 * @param email - Email address
 * @param subject - Optional email subject
 * @returns mailto: URL
 *
 * @example
 * ```ts
 * buildEmailUrl('info@example.com'); // 'mailto:info@example.com'
 * buildEmailUrl('info@example.com', 'Service Inquiry'); // 'mailto:info@example.com?subject=Service%20Inquiry'
 * ```
 */
export function buildEmailUrl(email: string, subject?: string): string {
  const mailto = `mailto:${email.trim()}`;

  if (subject) {
    return `${mailto}?subject=${encodeURIComponent(subject)}`;
  }

  return mailto;
}

/**
 * Extract domain from a URL for display purposes.
 *
 * @param url - Full URL
 * @returns Domain name without protocol or path
 *
 * @example
 * ```ts
 * extractDomain('https://www.example.com/path'); // 'www.example.com'
 * extractDomain('http://example.com'); // 'example.com'
 * ```
 */
export function extractDomain(url: string): string {
  try {
    const normalized = ensureHttps(url);
    const urlObj = new URL(normalized);
    return urlObj.hostname;
  } catch {
    // If URL parsing fails, try basic extraction
    const withoutProtocol = url.replace(/^(https?:\/\/)?/, '');
    const domainPart = withoutProtocol.split('/')[0];
    return domainPart || url;
  }
}

/**
 * Check if a string is a valid URL.
 *
 * @param url - String to validate
 * @returns True if the string is a valid URL
 *
 * @example
 * ```ts
 * isValidUrl('https://example.com'); // true
 * isValidUrl('not-a-url'); // false
 * isValidUrl('example.com'); // true (will be treated as valid domain)
 * ```
 */
export function isValidUrl(url: string): boolean {
  try {
    const normalized = ensureHttps(url);
    new URL(normalized);
    return true;
  } catch {
    return false;
  }
}

/**
 * Build a full address string from address components.
 *
 * @param components - Address components
 * @returns Formatted full address string
 *
 * @example
 * ```ts
 * buildFullAddress({
 *   street: '123 Main St',
 *   city: 'Columbus',
 *   state: 'OH',
 *   zipCode: '43215'
 * });
 * // Returns: '123 Main St, Columbus, OH 43215'
 * ```
 */
export function buildFullAddress(components: {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}): string {
  const parts: string[] = [];

  if (components.street) {
    parts.push(components.street);
  }

  if (components.city) {
    parts.push(components.city);
  }

  // Combine state and zip
  const stateZip = [components.state, components.zipCode]
    .filter(Boolean)
    .join(' ');

  if (stateZip) {
    parts.push(stateZip);
  }

  return parts.join(', ');
}
